/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Withdrawals Service - Super Admin Withdrawals API
 * ============================================================================
 *
 * Service layer for partner withdrawal management API calls.
 *
 * ============================================================================
 */

import { get, post } from '@/services/api/client';
import { ApiResponse, PaginatedResponse, PaginationParams, SpringPageResponse, transformPageResponse } from '@/services/api/types';
import { Withdrawal, WithdrawalFilterParams, WithdrawalActionRequest } from '@/types/superadmin.types';

const BASE_URL = '/superadmin/withdrawals';

/**
 * Backend withdrawal response (includes nested partner object)
 */
interface BackendWithdrawal {
  id: string;
  partnerId: string;
  partner?: {
    id: string;
    companyName: string;
    code: string;
  };
  referenceNumber: string;
  amount: number;
  currency: string;
  status: string;
  paymentMethod: string;
  paymentDetails: string;
  partnerNotes?: string;
  adminNotes?: string;
  rejectionReason?: string;
  requestedAt: string;
  approvedAt?: string;
  paidAt?: string;
  processedAt?: string;
  processedBy?: string;
}

/**
 * Transform backend withdrawal to frontend format
 */
function transformWithdrawal(backend: BackendWithdrawal): Withdrawal {
  return {
    id: backend.id,
    partnerId: backend.partnerId,
    partnerName: backend.partner?.companyName || 'Unknown Partner',
    amount: Number(backend.amount),
    currency: backend.currency,
    paymentMethod: backend.paymentMethod,
    paymentDetails: backend.paymentDetails,
    status: backend.status as Withdrawal['status'],
    requestedAt: backend.requestedAt,
    processedAt: backend.processedAt,
    processedBy: backend.processedBy,
    notes: backend.partnerNotes || backend.adminNotes,
    createdAt: backend.requestedAt, // Use requestedAt as createdAt
    updatedAt: backend.processedAt || backend.requestedAt, // Use processedAt or fallback to requestedAt
  };
}

/**
 * Withdrawals service for Super Admin
 */
export const withdrawalsService = {
  /**
   * Get paginated list of withdrawals
   * Backend uses path parameter for status: /status/{status}
   */
  async getWithdrawals(
    params?: PaginationParams & WithdrawalFilterParams
  ): Promise<PaginatedResponse<Withdrawal>> {
    // Extract status and build correct URL
    const { status, ...paginationParams } = params || {};

    // Use /status/{status} endpoint if status is provided, otherwise get all
    const url = status ? `${BASE_URL}/status/${status}` : BASE_URL;

    const response = await get<ApiResponse<SpringPageResponse<BackendWithdrawal>>>(url, paginationParams);
    const transformed = transformPageResponse(response.data);
    return {
      ...transformed,
      content: transformed.content.map(transformWithdrawal),
    };
  },

  /**
   * Get withdrawal by ID
   */
  async getWithdrawal(id: string): Promise<Withdrawal> {
    const response = await get<ApiResponse<Withdrawal>>(`${BASE_URL}/${id}`);
    return response.data;
  },

  /**
   * Approve a withdrawal request
   */
  async approveWithdrawal(id: string, data?: WithdrawalActionRequest): Promise<Withdrawal> {
    const response = await post<ApiResponse<Withdrawal>>(`${BASE_URL}/${id}/approve`, data);
    return response.data;
  },

  /**
   * Reject a withdrawal request
   */
  async rejectWithdrawal(id: string, data?: WithdrawalActionRequest): Promise<Withdrawal> {
    const response = await post<ApiResponse<Withdrawal>>(`${BASE_URL}/${id}/reject`, data);
    return response.data;
  },

  /**
   * Mark a withdrawal as paid
   */
  async markWithdrawalPaid(id: string, data?: WithdrawalActionRequest): Promise<Withdrawal> {
    const response = await post<ApiResponse<Withdrawal>>(`${BASE_URL}/${id}/pay`, data);
    return response.data;
  },

  /**
   * Get withdrawal statistics
   */
  async getWithdrawalStats(): Promise<{
    pendingCount: number;
    pendingAmount: number;
    approvedCount: number;
    paidThisMonth: number;
  }> {
    const response = await get<ApiResponse<{
      pendingCount: number;
      pendingAmount: number;
      approvedCount: number;
      paidThisMonth: number;
    }>>(`${BASE_URL}/stats`);
    return response.data;
  },
};

export default withdrawalsService;
